import React, { memo, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ASSET_URI, limitCharecters } from "../lib/utils";
import { IoBookOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
  Button,
} from "@material-tailwind/react";
import { FaEye, FaRegThumbsUp } from "react-icons/fa6";
import { FaStar } from "react-icons/fa";
import { MdOutlineAccessTimeFilled } from "react-icons/md";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import jalaali from "jalaali-js";

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  exit: { opacity: 0, y: 50 },
};

function formatDateByLang(rawDate, lang) {
  if (!rawDate) return "";
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return "";

  // treat these language prefixes as Shamsi/Jalali
  const shamsiPrefixes = ["ar", "pr", "ps", "fa", "prs"];
  const isShamsi = shamsiPrefixes.some((p) =>
    lang?.toLowerCase?.().startsWith?.(p)
  );

  if (!isShamsi) {
    // Gregorian (Meladi) for non-Shamsi languages — date only, no time
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(date);
  }

  // Jalali (Shamsi) — date only, no time
  const { jy, jm, jd } = jalaali.toJalaali(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate()
  );
  const dariMonths = [
    "حمل",
    "ثور",
    "جوزا",
    "سرطان",
    "اسد",
    "سنبله",
    "میزان",
    "عقرب",
    "قوس",
    "جدی",
    "دلو",
    "حوت",
  ];
  const monthName = dariMonths[jm - 1] ?? jm;
  return `${jd} ${monthName} ${jy}`;
}

const CourseItemInner = ({ course }) => {
  const { t, i18n } = useTranslation();

  const createdAtFormatted = useMemo(
    () => formatDateByLang(course?.createdAt, i18n.language || ""),
    [course?.createdAt, i18n.language]
  );

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const renderCardHeader = useCallback(() => {
    const categoryLabel = course?.category?.name?.[i18n.language] ?? "";
    return (
      <CardHeader color="blue-gray" className="relative h-auto">
        <img
          src={course?.image ? `${ASSET_URI}/${course.image}` : undefined}
          alt={course?.title?.[i18n.language] ?? "course"}
          className="h-48 w-full rounded-t-lg object-cover sm:h-52"
          loading="lazy"
          crossOrigin="anonymous"
        />
        <Button
          variant="filled"
          color="teal"
          size="md"
          className={`absolute text-xs rounded-md p-1 ${
            i18n.language === "en" ? "left-1" : "right-1"
          } top-1.5`}
          style={{ backgroundColor: "#334780", opacity: 0.85 }}
          aria-label={categoryLabel}
        >
          <span className="text-xs">{categoryLabel}</span>
        </Button>
      </CardHeader>
    );
  }, [course, i18n.language]);

  const renderCardBody = useCallback(() => {
    const subCat =
      course?.category?.subCategories?.[0]?.[i18n.language]?.trim?.() ?? "";
    const titleFull = course?.title?.[i18n.language]?.trim?.() ?? "";
    const title = limitCharecters(titleFull, 27);
    const chapterText = `${course?.chapter ?? ""} ${t("videos")}`;
    const subCatShort = limitCharecters(subCat, 18);
    const university = course?.university?.name?.[i18n.language] || course?.university?.name?.en || course?.university?.name?.pr || course?.university?.name?.ps || "";
    const isTitleTruncated = titleFull && title !== titleFull;
    const isSubCatTruncated = subCat && subCatShort !== subCat;

    return (
      <CardBody className="p-5">
        <div className="flex justify-start items-center gap-2 pb-2 text-gray-500">
          <IoBookOutline size={15} />
          <p className="text-sm">{chapterText}</p>

          <div className="border-l border-gray-300 h-4 mx-2" />

          <p className="text-sm" title={isSubCatTruncated ? subCat : undefined}>
            {" "}
            {subCatShort}
          </p>
        </div>

        <Typography
          variant="h5"
          color="blue-gray"
          className="text-md font-bold tracking-tight"
          title={isTitleTruncated ? titleFull : undefined}
        >
          {title}
        </Typography>

        {university ? <p className="mt-1 truncate text-sm text-indigo-700 dark:text-indigo-300" title={university}>{university}</p> : null}

        <div className="mt-2">
          <span
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            style={{ unicodeBidi: "isolate" }}
            className="text-sm text-gray-600"
          >
            {createdAtFormatted}
          </span>
        </div>

        <div className="mt-2">
          <span className="mx-2 capitalize inline-flex items-center rounded-sm bg-teal-50 px-1.5 py-0 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-teal-500/20">
            {course?.videoLang}
          </span>
        </div>

        <div className="flex justify-between items-center py-2 mt-4">
          <div className="flex flex-col items-center">
            <FaStar cursor="pointer" title="Rating" size={15} color="orange" />
            <p className="text-sm text-center text-black">
              {typeof course?.averageRating === "number"
                ? course.averageRating.toFixed(1)
                : "-"}
            </p>
          </div>

          <div className="flex flex-col justify-center items-center">
            <FaEye cursor="pointer" title="Views" size={15} color="#00BCD5" />
            <p className="text-sm text-center text-black">
              {course?.views ?? 0}
            </p>
          </div>

          <div className="flex flex-col justify-center items-center">
            <FaRegThumbsUp title={t("Like")} size={15} className="text-blue-600" />
            <p className="text-sm text-center text-black">
              {(course?.likesCount ?? course?.likes?.length) ?? 0}
            </p>
          </div>

          <div className="flex flex-col justify-center items-center">
            <MdOutlineAccessTimeFilled
              cursor="pointer"
              title="Duration"
              size={17}
              color="#607D8B"
            />
            <p className="text-sm text-center text-black">
              {course?.duration ?? "-"}
            </p>
          </div>
        </div>
      </CardBody>
    );
  }, [course, i18n.language, t, createdAtFormatted]);

  return (
    <Link
      to={`/courses/${course?._id}`}
      aria-label={course?.title?.[i18n.language] ?? "course link"}
    >
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={fadeIn}
        exit="exit"
      >
        <Card
          className="m-0 h-full max-w-none rounded-xl border border-gray-200 bg-white shadow-sm transition duration-300 ease-in-out hover:-translate-y-1 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800"
          dir={i18n.language === "en" ? "ltr" : "rtl"}
        >
          {renderCardHeader()}
          {renderCardBody()}
        </Card>
      </motion.div>
    </Link>
  );
};

function areEqual(prevProps, nextProps) {
  const prev = prevProps.course || {};
  const next = nextProps.course || {};
  if (prev._id !== next._id) return false;
  if (prev.views !== next.views) return false;
  if ((prev.likesCount ?? prev.likes?.length ?? 0) !== (next.likesCount ?? next.likes?.length ?? 0)) return false;
  if (prev.averageRating !== next.averageRating) return false;
  return true;
}

export default memo(CourseItemInner, areEqual);
